import {Button, DialogContentText, Tooltip} from '@mui/material';
import {useCagesWithUnallocatedBiomass, useGetPlan} from 'hooks/swr';
import useParseURL from 'hooks/useParseURL';
import {isInProgressState} from 'hooks/usePlanOptimization';
import PlayCircleRoundedIcon from '@mui/icons-material/PlayCircleRounded';
import {useTranslation} from 'react-i18next';
import {useState} from 'react';
import {type OptimizationRun} from 'client';
import {ConfirmationDialog, IconType} from 'components/Generic/Dialogs/ConfirmationDialog';

const OptimizationButton = ({
  triggerPlanOptimization,
  optimizationRun,
}: {
  triggerPlanOptimization: (planId: string) => Promise<void>;
  optimizationRun: OptimizationRun | undefined;
}) => {
  const {t} = useTranslation('common');
  const {planId} = useParseURL();
  const {data: plan} = useGetPlan({resourceId: planId});
  const [open, setOpen] = useState(false);
  const runInProgress = optimizationRun && isInProgressState(optimizationRun.state);
  const {data: unallocatedBiomass} = useCagesWithUnallocatedBiomass({resourceId: planId});
  const _unallocatedBiomass = unallocatedBiomass && unallocatedBiomass?.length > 0;
  const buttonTooltip = _unallocatedBiomass ? t('plan has unallocated biomass') : (t('optimizePlanTooltip') ?? '');

  if (!plan) {
    return null;
  }

  return (
    <>
      <Tooltip title={buttonTooltip}>
        <span>
          <Button
            disabled={!!(optimizationRun && runInProgress) || plan.active === false || _unallocatedBiomass}
            size="small"
            onClick={() => {
              setOpen(true);
            }}
            endIcon={<PlayCircleRoundedIcon />}
            variant="outlined"
            color="primary"
            id="onboarding-main-optimization"
          >
            {t('notOptimized')}
          </Button>
        </span>
      </Tooltip>
      <ConfirmationDialog
        open={open}
        icon={IconType.START}
        title={t('startOptimizationDialogTitle')}
        content={<DialogContentText>{t('startOptimizationDialogMessage')}</DialogContentText>}
        handleClose={() => {
          setOpen(false);
        }}
        handleConfirm={() => {
          triggerPlanOptimization(planId);
          setOpen(false);
        }}
      />
    </>
  );
};

export default OptimizationButton;
